import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from 'recharts';
import { CHART_MARGIN } from '../common';
import formatFloatNum from '../../../../shared/utils/floatFormatter';
import useTimeZone from '../../../../entities/timeZone/hooks/useTimeZone';
import utcToMs from '../../../../shared/utils/utcToMs';

// Статический линейный график Recharts для отображения данных сенсора
function StaticRechartsLine({ chartData, sensorName, width, height, thresholds, domain }) {
  const utc = useTimeZone();

  const hasData = Array.isArray(chartData) && chartData.length > 0;

  // ограничиваем кол-во подписей на оси X до 10
  const ticks = useMemo(() => {
    if (!domain) return [];

    const count = 10;
    const step = Math.max(1, Math.floor(chartData.length / count));
    const dataTicks = chartData.length
      ? chartData.filter((_, i) => i % step === 0).map((d) => d.timestamp)
      : [];

    const tickSet = new Set(dataTicks);
    tickSet.add(domain[0]);
    tickSet.add(domain[1]);

    return Array.from(tickSet).sort((a, b) => a - b);
  }, [chartData, domain]);

  return (
    <LineChart width={width} height={height} data={chartData} margin={CHART_MARGIN}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis
        type="number"
        dataKey="timestamp"
        domain={domain || ['dataMin', 'dataMax']}
        scale="time"
        ticks={ticks}
        tickFormatter={(timestamp) => {
          const shifted = timestamp + utcToMs(utc);
          return new Date(shifted).toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'UTC',
          });
        }}
      />
      <YAxis domain={['auto', 'auto']} />
      {hasData && (
        <Tooltip
          labelFormatter={(timestamp) => {
            const shifted = timestamp + utcToMs(utc);

            return new Date(shifted).toLocaleString('ru-RU', {
              timeZone: 'UTC',
            });
          }}
          formatter={(value) => [formatFloatNum(value, 2), 'Значение']}
        />
      )}

      {thresholds?.warningEnabled && thresholds.lowWarning != null && (
        <ReferenceLine
          y={thresholds.lowWarning}
          stroke="#ea580c"
          strokeWidth={1.5}
          ifOverflow="extendDomain"
        />
      )}
      {thresholds?.warningEnabled && thresholds.highWarning != null && (
        <ReferenceLine
          y={thresholds.highWarning}
          stroke="#ea580c"
          strokeWidth={1.5}
          ifOverflow="extendDomain"
        />
      )}

      {thresholds?.errorEnabled && thresholds.lowError != null && (
        <ReferenceLine
          y={thresholds.lowError}
          stroke="#b91c1c"
          strokeWidth={1.5}
          ifOverflow="extendDomain"
        />
      )}
      {thresholds?.errorEnabled && thresholds.highError != null && (
        <ReferenceLine
          y={thresholds.highError}
          stroke="#b91c1c"
          strokeWidth={1.5}
          ifOverflow="extendDomain"
        />
      )}

      <Line
        type="linear"
        dataKey="value"
        stroke="#3b82f6"
        name={sensorName}
        strokeWidth={1}
        dot={false}
        isAnimationActive={false}
      />
      <Legend />
    </LineChart>
  );
}

StaticRechartsLine.propTypes = {
  sensorName: PropTypes.string.isRequired,
  chartData: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      timestamp: PropTypes.number,
    }),
  ).isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  thresholds: PropTypes.shape({
    lowWarning: PropTypes.number,
    highWarning: PropTypes.number,
    lowError: PropTypes.number,
    highError: PropTypes.number,
    warningEnabled: PropTypes.bool,
    errorEnabled: PropTypes.bool,
  }),
  domain: PropTypes.arrayOf(PropTypes.number),
};

StaticRechartsLine.defaultProps = {
  width: 0,
  height: 0,
  thresholds: null,
  domain: undefined,
};

export default React.memo(StaticRechartsLine);
